import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Fuel, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (n) => `R ${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const todayIso = () => new Date().toISOString().slice(0, 10);
const shiftMonthDate = (iso, delta) => {
  const d = new Date(iso);
  d.setMonth(d.getMonth() + delta);
  return d.toISOString().slice(0, 10);
};
const shiftMonthStr = (yyyymm, delta) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Amber gradient for bars
const BAR_COLORS = ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5'];

function useFuelData(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    if (!url) return;
    setLoading(true);
    try {
      const r = await axios.get(url, { headers });
      setData(r.data);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, [url]);
  useEffect(() => { fetchData(); }, [fetchData]);
  return { data, loading, refetch: fetchData };
}

function UsageChartCard({ title, subtitle, iconColour, data, loading, controls }) {
  const rows = (data?.rows || []).map((r) => ({
    ...r,
    actual: r.verified_count > 0 ? r.verified_total : r.captured_total,
  }));
  const grand = data?.grand;
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <Fuel className={`h-4 w-4 ${iconColour}`} />
              {title}
            </CardTitle>
            <CardDescription className="text-xs mt-1">{subtitle}</CardDescription>
          </div>
          {controls}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {data && (
          <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-bold text-slate-600 uppercase">Period</div>
              <div className="text-sm font-bold text-slate-800">{data.cycle_label}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold text-slate-600 uppercase">Total usage</div>
              <div className="text-lg font-black text-orange-700">{money(grand?.captured_total)}</div>
              <div className="text-[10px] text-slate-500">{grand?.entry_count || 0} entries</div>
            </div>
          </div>
        )}
        <div style={{ width: '100%', height: 260 }}>
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-500 text-sm">No entries in this period</div>
          ) : (
            <ResponsiveContainer>
              <BarChart data={rows} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <XAxis dataKey="registration" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                <Tooltip
                  formatter={(v, name) => [money(v), name === 'actual' ? 'Actual usage' : name]}
                  labelFormatter={(l) => `Vehicle: ${l}`}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar dataKey="actual" radius={[4, 4, 0, 0]}>
                  {rows.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                  <LabelList dataKey="actual" position="top" formatter={(v) => `R${(v/1000).toFixed(1)}k`} fontSize={10} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function FuelBillingCycleWidget() {
  const [cycleEnd, setCycleEnd] = useState(todayIso());
  const [calMonth, setCalMonth] = useState(currentMonth());

  const cycleQuery = useFuelData(`${API}/fuel-tracker/billing-cycle?cycle_end=${cycleEnd}`);
  const monthQuery = useFuelData(`${API}/fuel-tracker/monthly-usage?month=${calMonth}`);

  const cycleControls = (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" onClick={() => setCycleEnd(shiftMonthDate(cycleEnd, -1))} data-testid="cycle-prev"><ChevronLeft className="h-3 w-3" /></Button>
      <Input type="date" value={cycleEnd} onChange={(e) => setCycleEnd(e.target.value)} className="h-8 w-40 text-xs" data-testid="cycle-end-input" />
      <Button size="sm" variant="outline" onClick={() => setCycleEnd(shiftMonthDate(cycleEnd, 1))} data-testid="cycle-next"><ChevronRight className="h-3 w-3" /></Button>
      <Button size="sm" variant="outline" onClick={() => setCycleEnd(todayIso())} className="text-xs" data-testid="cycle-today">Today</Button>
    </div>
  );

  const monthControls = (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="outline" onClick={() => setCalMonth(shiftMonthStr(calMonth, -1))} data-testid="month-prev"><ChevronLeft className="h-3 w-3" /></Button>
      <Input type="month" value={calMonth} onChange={(e) => setCalMonth(e.target.value)} className="h-8 w-36 text-xs" data-testid="month-input" />
      <Button size="sm" variant="outline" onClick={() => setCalMonth(shiftMonthStr(calMonth, 1))} data-testid="month-next"><ChevronRight className="h-3 w-3" /></Button>
      <Button size="sm" variant="outline" onClick={() => setCalMonth(currentMonth())} className="text-xs" data-testid="month-today">This</Button>
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Fuel className="h-5 w-5 text-orange-600" />
          Fuel Usage Charts
        </h2>
        <Link to="/admin/fuel-tracker" data-testid="fuel-tracker-link">
          <Button size="sm" variant="outline"><ExternalLink className="h-3 w-3 mr-1" /> Open Fuel Tracker</Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <UsageChartCard
          title="Billing Cycle (21st → 20th)"
          subtitle="Fuel supplier invoice period — actual usage per vehicle"
          iconColour="text-orange-600"
          data={cycleQuery.data}
          loading={cycleQuery.loading}
          controls={cycleControls}
        />
        <UsageChartCard
          title="Calendar Month (1st → last day)"
          subtitle="Full-month usage per vehicle for internal reporting"
          iconColour="text-blue-600"
          data={monthQuery.data}
          loading={monthQuery.loading}
          controls={monthControls}
        />
      </div>
    </div>
  );
}
